import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { FlagPair } from "./Markets";


const fmt = (n) =>
  n == null
    ? "—"
    : n >= 100
      ? n.toLocaleString("en-US", { maximumFractionDigits: 2 })
      : n.toFixed(4);

function GroupLabel({ children }) {
  return (
    <div className="col-span-full mt-6 flex items-center gap-4 first:mt-0">
      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{children}</span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}

function MetalBadge({ badge }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-gold/40 bg-gold/[0.07] font-tabular text-[10px] font-medium text-gold">
      {badge}
    </span>
  );
}

function Row({ r, i }) {
  const chg = r.change_pct;
  const up = (chg ?? 0) > 0;
  const flat = chg === 0;
  return (
    <motion.a
      href={r.kind === "fx" ? "/markets?tab=forex" : "/markets?tab=metals"}
      data-testid={`macro-row-${r.symbol.toLowerCase()}`}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.5, delay: Math.min(i, 10) * 0.04 }}
      className="group flex items-center justify-between gap-4 border-t border-white/10 py-3.5 transition-colors duration-300 hover:bg-white/[0.02]"
    >
      <span className="flex items-center gap-3 overflow-hidden">
        {r.kind === "fx" ? (
          <FlagPair flags={r.flags} symbol={r.symbol} />
        ) : r.badge ? (
          <MetalBadge badge={r.badge} />
        ) : (
          <span className="h-8 w-8 shrink-0" />
        )}
        <span className="flex items-baseline gap-2 overflow-hidden">
          <span className="text-sm text-gold transition-colors duration-300 group-hover:text-gold-light">
            {r.symbol}
          </span>
          <span className="hidden truncate text-[10px] uppercase tracking-[0.2em] text-white/55 md:inline">
            {r.name}
          </span>
          {r.kind === "metal" && (
            <BadgeCheck
              className="h-3.5 w-3.5 shrink-0 text-gold"
              data-testid={`metal-verified-${r.symbol.toLowerCase()}`}
              aria-label="Verified listing"
            />
          )}
        </span>
      </span>
      <span className="flex items-baseline gap-2.5">
        <span className="font-tabular text-sm text-white/85" data-testid={`macro-price-${r.symbol.toLowerCase()}`}>
          {r.kind === "fx" ? fmt(r.price) : `$${fmt(r.price)}`}
        </span>
        {chg != null && (
          <span className={`font-tabular text-[10px] ${flat ? "text-white/40" : up ? "text-[#00FF41]" : "text-[#FF003C]"}`}>
            {flat ? "•" : up ? "▲" : "▼"} {Math.abs(chg).toFixed(2)}%
          </span>
        )}
        <span className="text-[10px] uppercase tracking-[0.15em] text-white/30">{r.unit}</span>
      </span>
    </motion.a>
  );
}

export default function MacroBoard({ rows }) {
  if (!rows || rows.length === 0) return null;
  const metals = rows.filter((r) => r.kind === "metal");
  const fx = rows.filter((r) => r.kind === "fx");
  return (
    <section data-testid="macro-board" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">
            Metals & FX · Live
          </p>
          <h2 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
            Beyond
            <span className="italic text-gold"> crypto.</span>
          </h2>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-white/50">
          Twenty metals and twelve major currency pairs, quoted beside the
          digital book — the full Aureon picture.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="grid grid-cols-1 gap-x-12 border border-white/10 bg-[#141414]/40 px-6 py-2 backdrop-blur-xl sm:grid-cols-2"
      >
        <GroupLabel>Metals · {metals.length}</GroupLabel>
        {metals.map((r, i) => <Row key={r.symbol} r={r} i={i} />)}
        <GroupLabel>Foreign exchange · {fx.length}</GroupLabel>
        {fx.map((r, i) => <Row key={r.symbol} r={r} i={i} />)}
      </motion.div>
    </section>
  );
}
